import { useState, useCallback } from "react";
import { useBackButton } from "@/hooks/useBackButton";
import { Lightbulb, Send, Loader2, CheckCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const SupportDialog = () => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const closeDialog = useCallback(() => setOpen(false), []);
  useBackButton(open, closeDialog, "support");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async () => {
    if (!firstName.trim() || !lastName.trim() || !phone.trim() || !message.trim()) {
      toast({ title: "Preencha todos os campos", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const currentUserId = session?.user?.id ?? null;

      const { error } = await supabase.from("support_tickets").insert({
        customer_first_name: firstName.trim(),
        customer_last_name: lastName.trim(),
        customer_phone: phone.trim(),
        message: message.trim(),
        user_id: currentUserId,
      });
      if (error) throw error;
      setSubmitted(true);
    } catch {
      toast({ title: "Erro ao enviar. Tente novamente.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setFirstName("");
    setLastName("");
    setPhone("");
    setMessage("");
    setSubmitted(false);
  };

  const handleOpenChange = (v: boolean) => {
    setOpen(v);
    if (!v) handleReset();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <button className="p-2 text-foreground hover:text-primary transition-colors" title="Suporte">
          <Lightbulb className="w-5 h-5" />
        </button>
      </DialogTrigger>
      <DialogContent className="bg-card border-border max-w-md">
        <DialogHeader>
          <DialogTitle className="text-foreground font-display text-xl">Fale com o Suporte</DialogTitle>
        </DialogHeader>

        {submitted ? (
          <div className="text-center py-8 space-y-3">
            <CheckCircle className="w-12 h-12 text-primary mx-auto" />
            <p className="font-semibold text-foreground">Mensagem enviada!</p>
            <p className="text-sm text-muted-foreground">Entraremos em contato pelo seu WhatsApp.</p>
          </div>
        ) : (
          <div className="space-y-3 pt-2">
            <input
              type="text"
              placeholder="Nome *"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg bg-background border border-border text-foreground placeholder:text-muted-foreground text-sm focus:border-primary focus:outline-none"
              maxLength={50}
            />
            <input
              type="text"
              placeholder="Sobrenome *"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg bg-background border border-border text-foreground placeholder:text-muted-foreground text-sm focus:border-primary focus:outline-none"
              maxLength={50}
            />
            <input
              type="tel"
              placeholder="WhatsApp *"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg bg-background border border-border text-foreground placeholder:text-muted-foreground text-sm focus:border-primary focus:outline-none"
              maxLength={20}
            />
            <textarea
              placeholder="Descreva sua queixa ou dúvida *"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              className="w-full px-3 py-2.5 rounded-lg bg-background border border-border text-foreground placeholder:text-muted-foreground text-sm focus:border-primary focus:outline-none resize-none"
              maxLength={1000}
            />
            <button
              onClick={handleSubmit}
              disabled={submitting || !firstName.trim() || !lastName.trim() || !phone.trim() || !message.trim()}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-lg bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-4 h-4" /> Enviar</>}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SupportDialog;
